import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import type { AuthorshipEvidenceModel } from "@/api/cle-api";
import {
  mergeEvidenceDetailUpdate,
  mergeEvidenceDetailWithOverride,
  useAuthorship,
  useReclassifyReviewThread,
} from "@/hooks/cle-hooks";
import { showCleApiErrorToast } from "@/lib/showCleApiErrorToast";
import { useEffect, useMemo, useState } from "react";
import { useToaster } from "rsuite";

import {
  getReclassifyLocks,
  getMissingEvidenceFields,
} from "../person/id/Efteruddannelse/utils/approvalCompleteness";
import {
  DocumentClassificationActions,
  REPORTED_EVIDENCE_LOCKED_REASON,
} from "../person/id/Efteruddannelse/DocumentClassificationActions";
import { StatusBadge } from "../person/id/Efteruddannelse/StatusBadge";
import type { CourseCertificateDecision } from "../person/id/Efteruddannelse/types";
import {
  formatEvidenceStatus,
  getEvidenceStatusVariant,
} from "../person/id/Efteruddannelse/utils/evidenceStatus";
import { formatDate } from "../person/id/Efteruddannelse/utils/formatters";
import { AuthorshipValidation } from "./AuthorshipValidation";
import {
  DocumentAuditTrailPanel,
  DocumentCommentsPanel,
} from "./DocumentReviewPanels";
import { EvidenceReviewThreadTopPanel } from "./EvidenceReviewThreadTopPanel";
import { ReviewDetailLayout } from "./ReviewDetailLayout";
import { ReviewInfoCard, ReviewInfoField } from "./ReviewInfoCard";

interface AuthorshipReviewViewProps {
  userId: string;
  authorshipId: string;
  onBack: () => void;
}

export const AuthorshipReviewView = ({
  userId,
  authorshipId,
  onBack,
}: AuthorshipReviewViewProps) => {
  const { data, isLoading, error } = useAuthorship({ userId, authorshipId });
  const toaster = useToaster();
  const { mutate: reclassifyAuthorship, isPending: isReclassifying } =
    useReclassifyReviewThread();
  const [authorshipOverride, setAuthorshipOverride] = useState<
    AuthorshipEvidenceModel | undefined
  >(undefined);

  useEffect(() => {
    setAuthorshipOverride(undefined);
  }, [authorshipId]);

  const authorship = useMemo(
    () => mergeEvidenceDetailWithOverride(data, authorshipOverride),
    [authorshipOverride, data],
  );

  const handleAuthorshipUpdated = (updated: AuthorshipEvidenceModel) => {
    setAuthorshipOverride((prev) =>
      mergeEvidenceDetailUpdate(data ?? prev, updated),
    );
  };

  const handleReclassify = (decision: CourseCertificateDecision) => {
    reclassifyAuthorship(
      {
        subjectId: authorshipId,
        userId,
        newStatus: decision,
      },
      {
        onSuccess: () => setAuthorshipOverride(undefined),
        onError: (err) => showCleApiErrorToast(toaster, err),
      },
    );
  };

  return (
    <ReviewDetailLayout
      className="p-2"
      onBack={onBack}
      topPanel={
        <EvidenceReviewThreadTopPanel
          reviewThread={authorship?.reviewThread}
          ownerUserId={userId}
        />
      }
      loading={isLoading}
      error={
        error ? (
          <CleApiErrorText
            error={error}
            fallback="Kunne ikke hente forfatterskab."
          />
        ) : null
      }
      pdfPanel={
        authorship ? (
          <ReviewInfoCard title="Forfatterskabsoplysninger">
            <ReviewInfoField
              label="Status"
              value={
                <StatusBadge
                  pill
                  status={formatEvidenceStatus(authorship.status)}
                  variant={getEvidenceStatusVariant(authorship.status)}
                />
              }
            />
            <ReviewInfoField
              label="Indberettet"
              value={authorship.isReported ? "Ja" : "Nej"}
            />
            <ReviewInfoField
              label="Oprettet"
              value={formatDate(authorship.createdAt)}
            />
            <ReviewInfoField
              label="Oprettet af"
              value={authorship.createdByUserName ?? "-"}
            />
          </ReviewInfoCard>
        ) : null
      }
      auditPanel={<DocumentAuditTrailPanel subjectId={authorshipId} />}
      infoPanel={
        authorship ? (
          <>
            <AuthorshipValidation
              authorship={authorship}
              onAuthorshipUpdated={handleAuthorshipUpdated}
            />
          </>
        ) : null
      }
      actionsPanel={
        authorship ? (
          <DocumentClassificationActions
            isPending={isReclassifying}
            onReclassify={handleReclassify}
            {...getReclassifyLocks({
              isReported: authorship.isReported,
              missingFields: getMissingEvidenceFields(authorship),
              reportedReason: REPORTED_EVIDENCE_LOCKED_REASON,
            })}
          />
        ) : null
      }
      commentsPanel={
        <DocumentCommentsPanel
          subjectId={authorshipId}
          ownerUserId={userId}
          onCommentAdded={() => setAuthorshipOverride(undefined)}
          isReported={authorship?.isReported ?? false}
        />
      }
    />
  );
};
